/**
 * Razorpay Checkout helper for CartIQ 360.
 * Keeps all the script-load / popup orchestration in one place so that any
 * CTA (pricing card, compare-table footer, dashboard nudge) can call a single
 * `openRazorpayCheckout({plan, cycle})` and get a working paywall.
 */
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const SCRIPT_URL = "https://checkout.razorpay.com/v1/checkout.js";

let scriptPromise = null;
function loadRazorpayScript() {
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise((resolve, reject) => {
    if (typeof window === "undefined") return reject(new Error("ssr"));
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = SCRIPT_URL;
    s.async = true;
    s.onload = () => resolve(true);
    s.onerror = () => {
      scriptPromise = null;
      reject(new Error("razorpay-script-failed"));
    };
    document.body.appendChild(s);
  });
  return scriptPromise;
}

async function postJson(path, body) {
  const res = await fetch(`${API}${path}`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    let detail = `${path} failed`;
    let payload = null;
    try {
      const j = await res.json();
      payload = j?.detail;
      detail = typeof payload === "string" ? payload : payload?.message || detail;
    } catch {
      /* ignore */
    }
    const err = new Error(detail);
    err.status = res.status;
    err.detail = payload;
    throw err;
  }
  return res.json();
}

/**
 * Open the Razorpay Checkout modal for a given plan + cycle.
 * Returns the verification result (plan flipped on success) or throws.
 * @param {{plan:"starter"|"pro"|"family", cycle:"monthly"|"yearly"}} opts
 * @param {{onSuccess?:function, onFail?:function}} [callbacks]
 */
export async function openRazorpayCheckout({ plan, cycle }, callbacks = {}) {
  try {
    await loadRazorpayScript();
  } catch (e) {
    toast.error("Could not load payment window. Check your connection.");
    throw e;
  }

  let order;
  try {
    order = await postJson("/payments/create-order", { plan, cycle });
  } catch (e) {
    // D3: server returns 409 when the user is already on this plan. Show a
    // friendlier nudge instead of the generic failure toast.
    if (e?.status === 409 && e?.detail?.error === "already_on_plan") {
      const cur = (e.detail.currentPlan || "your current plan").toString();
      toast.info(
        `You're already on ${cur.charAt(0).toUpperCase() + cur.slice(1)}. Pick a higher tier to upgrade.`
      );
      const dup = new Error("already_on_plan");
      dup.code = "already_on_plan";
      dup.currentPlan = cur;
      throw dup;
    }
    // E1: server returns 409 for downgrades — surface a clear toast.
    if (e?.status === 409 && e?.detail?.error === "downgrade_not_supported") {
      toast.info(e.detail.message || "Cancel your current plan first to switch tiers.");
      const dn = new Error("downgrade_not_supported");
      dn.code = "downgrade_not_supported";
      dn.currentPlan = e.detail.currentPlan;
      dn.targetPlan = e.detail.targetPlan;
      throw dn;
    }
    toast.error(`Could not create order: ${e.message}`);
    throw e;
  }

  // E1: build the description to reflect a prorated upgrade (so the user sees
  // the upgrade context inside the Razorpay popup itself).
  const description = order.isUpgrade
    ? `Upgrade ${order.fromPlan ? order.fromPlan.toUpperCase() + " → " : ""}${order.planLabel} · prorated`
    : `${order.planLabel} — 15-day free trial`;

  return new Promise((resolve, reject) => {
    const rzp = new window.Razorpay({
      key: order.keyId,
      amount: order.amount,
      currency: order.currency,
      name: "CartIQ 360",
      description,
      order_id: order.razorpayOrderId,
      prefill: order.prefill,
      theme: { color: "#FF6B00" },
      notes: { plan: order.plan, cycle: order.cycle },
      handler: async (response) => {
        try {
          const verify = await postJson("/payments/verify", {
            razorpay_order_id: response.razorpay_order_id,
            razorpay_payment_id: response.razorpay_payment_id,
            razorpay_signature: response.razorpay_signature,
          });
          // E2: skip the silent toast — the celebration modal owns the moment.
          // Callers (Dashboard / PricingSection) now show
          // SubscriptionCelebrationModal via the onSuccess callback.
          if (callbacks.onSuccess) callbacks.onSuccess(verify);
          resolve(verify);
        } catch (err) {
          toast.error("Payment verification failed. Support has been notified.");
          if (callbacks.onFail) callbacks.onFail(err);
          reject(err);
        }
      },
      modal: {
        ondismiss: () => {
          if (callbacks.onFail) callbacks.onFail(new Error("dismissed"));
          reject(new Error("dismissed"));
        },
      },
    });

    rzp.on("payment.failed", (resp) => {
      toast.error(
        `Payment failed: ${resp?.error?.description || "please try again"}`
      );
      if (callbacks.onFail) callbacks.onFail(resp);
      reject(new Error("payment_failed"));
    });

    rzp.open();
  });
}
